import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { forbiddenText } from '../lib/forbiddenText'

// Abrechnung: Kurzliste der OFFENEN Rechnungen (Spec Informationsarchitektur, Abschnitt 5, QM-Bedingungen):
// - Quelle ist dieselbe ERP-Live-Liste wie Dokumente → Rechnungen (GET /erpnext/invoices), keine eigene Datenhaltung.
// - Belegnummer, Betrag und Status kommen unveraendert aus dem ERP (Status-Text des ERP, "offen" = outstanding_amount > 0,
//   nie aus Stripe), keine eigenen Summen.
// - Kein PDF-Link hier: der Beleg liegt an genau einer Stelle (Dokumente → Rechnungen, gleiche Org-Pruefung im Backend).

interface ErpInvoice {
  name: string
  status: string
  grand_total: number
  outstanding_amount: number
  currency: string
  posting_date: string
  due_date: string
}

interface Props {
  slugOrToken: string
}

const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–'

const fmtAmount = (n: number, c?: string) => `${n.toFixed(2).replace('.', ',')} ${c ?? 'EUR'}`

export default function OpenInvoices({ slugOrToken }: Props) {
  const { api: keycloakApi } = useAuth()
  const [open, setOpen] = useState<ErpInvoice[] | null>(null)
  const [state, setState] = useState<'loading' | 'ok' | 'unavailable'>('loading')
  const [forbidden, setForbidden] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    keycloakApi!
      .get(`/api/org/${slugOrToken}/erpnext/invoices`)
      .then((r) => {
        if (cancelled) return
        const raw = r.data?.invoices ?? []
        const list: ErpInvoice[] = Array.isArray(raw) ? raw : []
        setOpen(list.filter((i) => i.outstanding_amount > 0))
        setState('ok')
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setForbidden(forbiddenText(e))
        setState('unavailable')
      })
    return () => {
      cancelled = true
    }
  }, [slugOrToken, keycloakApi])

  if (state === 'loading') return null

  return (
    <section className="settings-section" style={{ marginBottom: '1.5rem', order: 1 }}>
      <div className="section-header">
        <h2>📑 Offene Rechnungen</h2>
        {state === 'ok' && <span className="section-badge">{open?.length ?? 0}</span>}
      </div>
      {state === 'unavailable' ? (
        <p role="status" style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
          {forbidden ?? 'Die Rechnungsliste ist derzeit nicht abrufbar.'}
        </p>
      ) : open && open.length === 0 ? (
        <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>Keine offenen Rechnungen.</p>
      ) : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '0.5rem' }}>Rechnung</th>
                <th style={{ padding: '0.5rem' }}>Fällig</th>
                <th style={{ padding: '0.5rem' }}>Status</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Offen</th>
              </tr>
            </thead>
            <tbody>
              {open!.map((i) => (
                <tr key={i.name} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.5rem' }}><code>{i.name}</code></td>
                  <td style={{ padding: '0.5rem' }}>{fmtDate(i.due_date)}</td>
                  <td style={{ padding: '0.5rem' }}>{i.status}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtAmount(i.outstanding_amount, i.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ margin: '0.75rem 0 0', fontSize: '0.8125rem' }}>
            Belege (PDF) finden Sie unter <Link to="../dokumente/rechnungen">Dokumente → Rechnungen</Link>.
          </p>
        </>
      )}
    </section>
  )
}
